import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import { catchError, map, mapTo } from 'rxjs/operators';
import { QueryPartData } from '../../advanced-search/filters/base/entities/query-part-data.entity';
import { Paginated } from '../../core/common/entities/mediahaven-api/paginated.entity';
import { CustomHttpParamEncoder } from '../../core/http/entities/custom-http-param-encoder';
import { ShAbstractHttpService } from '../../core/http/services/http.abstract.service';
import { StructuredQuery } from '../../core/query/entities/structured-query.entity';
import { QueryParser } from '../../core/query/helpers/query-parser.helper';
import { makeSequentialRequests } from '../../helpers/observables/make-sequential-requests.helper';
import { SortInfo } from '../../overviews/generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { PaginatedResults } from '../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { PaginationInfo } from '../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { mapToPaginatedResult } from '../../overviews/generic-overview/components/pagination/helpers/map-to-paginated-result.helper';
import { ISavedQuery, QueryInfo, SavedQuery, SavedQueryId } from '../entities/saved-query.entity';
import { StructuredQueryToApiStructuredQuery } from '../helpers/convert-structured-query.helper';
import { omit } from 'lodash-es';
import { FixStructuredQuery } from '../helpers/saved-query-validation.helper';

@Injectable({ providedIn: 'root' })
export class ShSavedQueriesService {
    private readonly queryParser: QueryParser = inject(QueryParser);

    constructor(protected http: ShAbstractHttpService) {}

    private readonly filtersEndpoint: string = `v2/filters`;

    // #region Saved Queries
    public getSavedQueries(
        filters: QueryPartData[],
        paginationInfo: PaginationInfo,
        sortInfo: SortInfo
    ): Observable<PaginatedResults<SavedQuery>> {
        let params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() })
            .set('fieldKeyType', 'DottedKey')
            .set('startIndex', (paginationInfo.page * paginationInfo.resultsPerPage).toString())
            .set('nrOfResults', paginationInfo.resultsPerPage.toString())
            .set('sort', sortInfo.field)
            .set('direction', sortInfo.direction);

        filters.forEach(filter => {
            if (filter.field === 'name') {
                params = params.set('name', `*${filter.values.join(',')}*`);
            }
        });

        const request = `${this.filtersEndpoint}?${params.toString()}`;

        return this.http.get<Paginated<ISavedQuery>>(request).pipe(
            map(response => ({
                ...response,
                Results: response.Results.map(savedQuery => new SavedQuery(savedQuery, this.queryParser))
            })),
            map(response => mapToPaginatedResult(response, paginationInfo.resultsPerPage))
        );
    }

    public getSavedQuery(savedQueryId: SavedQueryId): Observable<SavedQuery> {
        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set(
            'fieldKeyType',
            'DottedKey'
        );

        const request = `${this.filtersEndpoint}/${savedQueryId}?${params.toString()}`;

        return this.http
            .get<ISavedQuery>(request)
            .pipe(map(savedQuery => new SavedQuery(savedQuery, this.queryParser)));
    }

    public saveQuery(structuredQuery: StructuredQuery, queryInfo: QueryInfo): Observable<ISavedQuery> {
        const queryToSave = {
            ...queryInfo,
            StructuredQuery: StructuredQueryToApiStructuredQuery(structuredQuery)
        };
        return this.http.post<ISavedQuery>(this.filtersEndpoint, queryToSave);
    }

    public updateSavedQuery(savedQuery: SavedQuery): Observable<SavedQuery> {
        const savedQueryId: SavedQueryId = savedQuery.Id;
        const request = `${this.filtersEndpoint}/${savedQueryId}`;

        const trimmedSavedQuery: ISavedQuery = {
            ...omit(savedQuery, ['ValidQuery']),
            StructuredQuery: StructuredQueryToApiStructuredQuery(savedQuery.StructuredQuery)
        };

        return this.http.put<ISavedQuery>(request, trimmedSavedQuery).pipe(
            catchError(error => {
                // If the call fails with bad params, try fixing the saved query once and retry.
                if (error?.status === 400 && error?.error?.Code === 'EPARAMINV') {
                    const fixedSavedQuery: ISavedQuery = {
                        ...trimmedSavedQuery,
                        StructuredQuery: StructuredQueryToApiStructuredQuery(
                            FixStructuredQuery(savedQuery.StructuredQuery)
                        )
                    };
                    return this.http.put<ISavedQuery>(request, fixedSavedQuery);
                }
                return throwError(() => error);
            }),
            map(updatedQuery => new SavedQuery(updatedQuery, this.queryParser))
        );
    }

    public deleteSavedQuery(savedQuery: SavedQuery): Observable<boolean> {
        const savedQueryId: SavedQueryId = savedQuery.Id;
        const request = `${this.filtersEndpoint}/${savedQueryId}`;
        return this.http.delete<boolean>(request).pipe(mapTo(true));
    }

    public bulkDeleteSavedQueries(toDelete: SavedQuery[]): Observable<boolean[]> {
        const requests$: Observable<boolean>[] = toDelete.map(this.deleteSavedQuery.bind(this));

        return makeSequentialRequests(requests$);
    }
    // #endregion
}
